import {
  ArrowLeftIcon,
  CalendarIcon,
  CheckCircleIcon,
  CurrencyRupeeIcon,
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon,
  StarIcon,
  TrophyIcon,
  UserGroupIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import PhoneActions from '../../components/PhoneActions';

const AgentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAgentDetails();
  }, [id]);

  const fetchAgentDetails = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/agents/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setAgent(response.data.data);
      } else {
        toast.error('Failed to load agent details');
      }
    } catch (error) {
      console.error('Error fetching agent:', error);
      toast.error('Error loading agent details');
      navigate('/agents');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/agents')}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-3xl font-display font-bold text-gray-900">Agent Not Found</h1>
          </div>
        </div>
      </div>
    );
  }

  const getAgentName = () => {
    return `${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.name || 'Unknown Agent';
  };

  const stats = agent.stats || {
    activeSellers: 0,
    totalCarsListed: 0,
    carsSold: 0,
    totalCommission: 0
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/agents')}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeftIcon className="w-6 h-6" />
        </button>
        <div>
          <h1 className="text-3xl font-display font-bold text-gray-900">
            {getAgentName()}
          </h1>
          <p className="text-gray-600 mt-2">
            Comprehensive agent profile and performance metrics
          </p>
        </div>
      </div>

      {/* Agent Profile Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl p-8 shadow-soft border border-gray-200"
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Section */}
          <div className="lg:col-span-1">
            <div className="flex flex-col items-center">
              <img
                src={agent.profileImage || 'https://via.placeholder.com/150'}
                alt={getAgentName()}
                className="w-32 h-32 rounded-xl object-cover border-4 border-primary-100 mb-4"
              />
              <h2 className="text-2xl font-bold text-gray-900 text-center">{getAgentName()}</h2>
              <div className={`mt-3 px-3 py-1 rounded-full text-sm font-medium ${
                agent.isActive 
                  ? 'bg-success-100 text-success-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {agent.isActive ? '✓ Active' : 'Inactive'}
              </div>
              
              {/* Membership Badge */}
              {agent.membership && (
                <div className="mt-3 px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r from-yellow-400 to-yellow-600 text-white flex items-center gap-2">
                  <StarIcon className="w-4 h-4" />
                  {typeof agent.membership === 'string' 
                    ? agent.membership.charAt(0).toUpperCase() + agent.membership.slice(1)
                    : agent.membership.type?.charAt(0).toUpperCase() + agent.membership.type?.slice(1)
                  }
                </div>
              )}
            </div>
          </div>

          {/* Contact & Info Section */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-2 gap-6">
              {/* Email */}
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <EnvelopeIcon className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Email</p>
                  <p className="mt-1 text-gray-900 font-medium break-all">{agent.email || 'N/A'}</p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <PhoneIcon className="w-6 h-6 text-success-600" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Phone</p>
                  <div className="mt-1"><PhoneActions phone={agent.phone || agent.phoneNumber} size="md" /></div>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <MapPinIcon className="w-6 h-6 text-secondary-600" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Location</p>
                  <p className="mt-1 text-gray-900 font-medium">
                    {typeof agent.location === 'object' && agent.location
                      ? `${agent.location.city || ''}, ${agent.location.state || ''}`.replace(/^,\s*/, '').replace(/,\s*$/, '')
                      : agent.location || 'N/A'
                    }
                  </p>
                </div>
              </div>

              {/* Joined Date */}
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <CalendarIcon className="w-6 h-6 text-warning-600" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Joined</p>
                  <p className="mt-1 text-gray-900 font-medium">
                    {new Date(agent.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Status</p>
                  <p className="mt-1 text-gray-900 font-medium">{agent.isActive ? 'Active' : 'Inactive'}</p>
                </div>
              </div>

              {/* Commission */}
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <CurrencyRupeeIcon className="w-6 h-6 text-success-600" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Total Commission</p>
                  <p className="mt-1 text-gray-900 font-medium">₹{(stats.totalCommission || 0).toLocaleString('en-IN')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Active Sellers */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200 hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Sellers</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">{stats.activeSellers}</p>
              <p className="mt-2 text-xs text-gray-500">Sellers created by this agent</p>
            </div>
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary-100">
                <UserGroupIcon className="h-6 w-6 text-primary-600" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Total Cars Listed */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200 hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Cars Listed</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">{stats.totalCarsListed}</p>
              <p className="mt-2 text-xs text-gray-500">Total cars from all sellers</p>
            </div>
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-secondary-100">
                <UserIcon className="h-6 w-6 text-secondary-600" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Cars Sold */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200 hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Cars Sold</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">{stats.carsSold}</p>
              <p className="mt-2 text-xs text-gray-500">Successfully sold cars</p>
            </div>
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-success-100">
                <TrophyIcon className="h-6 w-6 text-success-600" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Sales Conversion Rate */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200 hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {stats.totalCarsListed > 0 
                  ? ((stats.carsSold / stats.totalCarsListed) * 100).toFixed(1)
                  : '0'
                }%
              </p>
              <p className="mt-2 text-xs text-gray-500">Sales efficiency</p>
            </div>
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-warning-100">
                <StarIcon className="h-6 w-6 text-warning-600" />
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Performance Details */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white rounded-xl p-8 shadow-soft border border-gray-200"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Performance Summary</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Key Metrics */}
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <span className="text-gray-600 font-medium">Active Sellers</span>
              <span className="text-2xl font-bold text-primary-600">{stats.activeSellers}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <span className="text-gray-600 font-medium">Cars Listed</span>
              <span className="text-2xl font-bold text-secondary-600">{stats.totalCarsListed}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <span className="text-gray-600 font-medium">Cars Sold</span>
              <span className="text-2xl font-bold text-success-600">{stats.carsSold}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <span className="text-gray-600 font-medium">Avg Cars per Seller</span>
              <span className="text-2xl font-bold text-warning-600">
                {stats.activeSellers > 0 ? (stats.totalCarsListed / stats.activeSellers).toFixed(1) : '0'}
              </span>
            </div>
          </div>

          {/* Status & Info */}
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <span className="text-gray-600 font-medium">Member Since</span>
              <span className="text-sm font-semibold text-gray-900">
                {new Date(agent.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
              </span>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <span className="text-gray-600 font-medium">Account Status</span>
              <span className={`text-sm font-semibold ${agent.isActive ? 'text-success-600' : 'text-gray-600'}`}>
                {agent.isActive ? '✓ Active' : 'Inactive'}
              </span>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <span className="text-gray-600 font-medium">Membership Plan</span>
              <span className="text-sm font-semibold text-primary-600">
                {agent.membership 
                  ? (typeof agent.membership === 'string' 
                      ? agent.membership.charAt(0).toUpperCase() + agent.membership.slice(1)
                      : agent.membership.type?.charAt(0).toUpperCase() + agent.membership.type?.slice(1))
                  : 'Basic'
                }
              </span>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <span className="text-gray-600 font-medium">Total Commission</span>
              <span className="text-sm font-semibold text-success-600">₹{(stats.totalCommission || 0).toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AgentDetails;